import React, { useCallback, useEffect, useState, useMemo } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../hooks/auth';
import { useCustomTheme } from '../../../hooks/useCustomTheme';
import { RelatorioFiltroPeriodo } from '../../../components/RelatorioFiltroPeriodo';
import Chart from 'react-google-charts';
import { NotificationManager } from 'react-notifications';
import { AutoComplete, Whisper, Tooltip as HelpTooltip } from 'rsuite';
import { FaInfoCircle } from 'react-icons/fa';
import { CustomSelectPicker } from '../../../components/CustomSelectPicker';
import { CustomButton } from '../../../components/CustomButton';
import {
  ChartCard,
  ChartTitleRow,
  PageWrapper,
  RelatorioPeriodoVendas,
  PageHeader,
  PageTitle,
  DashboardGrid,
  KpiLabel,
  ExplorarCard,
  FiltrosExplorar,
  Podio,
  PodioColuna,
  PodioNome,
  PodioQuantidade,
  PodioBloco,
  PodioRestante,
  PodioRestanteLinha,
} from './styles';

const DIAS_SEMANA = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
];

const ESTILO_PODIO = [
  { altura: 140, cor: '#E0A800' },
  { altura: 100, cor: '#8E9AA6' },
  { altura: 70, cor: '#B5651D' },
];

const formatarHora = (hora) => `${String(hora).padStart(2, '0')}:00`;

const OPCOES_HORA = Array.from({ length: 24 }, (_, hora) => ({
  label: formatarHora(hora),
  value: hora,
}));

const normalizar = (texto) =>
  texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const indiceDoMaior = (lista) => lista.indexOf(Math.max(...lista));

const formatarData = (data) =>
  data.getFullYear() +
  '-' +
  ('00' + (data.getMonth() + 1)).slice(-2) +
  '-' +
  ('00' + data.getDate()).slice(-2);

const periodoDoMes = () => {
  const hoje = new Date();

  return {
    inicio: formatarData(new Date(hoje.getFullYear(), hoje.getMonth(), 1)),
    fim: formatarData(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)),
  };
};

const Ajuda = ({ texto }) => {
  const { theme } = useCustomTheme();

  return (
    <Whisper
      placement="bottom"
      trigger="hover"
      speaker={<HelpTooltip>{texto}</HelpTooltip>}
    >
      <span style={{ cursor: 'help', display: 'flex' }}>
        <FaInfoCircle size={12} color={theme.colors.textSecondary} />
      </span>
    </Whisper>
  );
};

const VendasPorPeriodoHorario = () => {
  const { id_empresa } = useAuth();
  const { theme } = useCustomTheme();
  const [periodo, setPeriodo] = useState(periodoDoMes);
  const [dataInicio, setDataInicio] = useState(periodo.inicio);
  const [dataFim, setDataFim] = useState(periodo.fim);
  const [carregando, setCarregando] = useState(false);
  const [linhas, setLinhas] = useState([]);
  const [produtosHorario, setProdutosHorario] = useState([]);
  const [horaSelecionada, setHoraSelecionada] = useState(null);
  const [busca, setBusca] = useState('');

  const aoBuscarProduto = (valor) => {
    setBusca(valor);
    if (valor !== '') setHoraSelecionada(null);
  };

  const aoEscolherHora = (valor) => {
    setHoraSelecionada(valor);
    if (valor !== null) setBusca('');
  };

  const { porDia, porHora } = useMemo(() => {
    const dias = Array.from({ length: 7 }, () => ({ quantidade: 0, total: 0 }));
    const horas = Array.from({ length: 24 }, () => ({
      quantidade: 0,
      total: 0,
    }));
    linhas.forEach((linha) => {
      const quantidade = Number(linha.quantidade);
      const total = Number(linha.total);
      const dia = dias[Number(linha.diaSemana) - 1];
      const hora = horas[Number(linha.hora)];

      if (dia) {
        dia.quantidade += quantidade;
        dia.total += total;
      }
      if (hora) {
        hora.quantidade += quantidade;
        hora.total += total;
      }
    });
    return { porDia: dias, porHora: horas };
  }, [linhas]);

  const dadosDias = useMemo(
    () => [
      ['Dia', 'Pedidos'],
      ...porDia.map((dia, index) => [DIAS_SEMANA[index], dia.quantidade]),
    ],
    [porDia],
  );

  const dadosHoras = useMemo(
    () => [
      ['Hora', 'Pedidos'],
      ...porHora.map((hora, index) => [formatarHora(index), hora.quantidade]),
    ],
    [porHora],
  );

  const sugestoesProdutos = useMemo(
    () =>
      [...new Set(produtosHorario.map((p) => p.descricao))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [produtosHorario],
  );

  const vendasProduto = useMemo(() => {
    const termo = normalizar(busca.trim());
    if (termo === '') return null;

    const dias = Array.from({ length: 7 }, () => 0);
    const horas = Array.from({ length: 24 }, () => 0);

    produtosHorario
      .filter((p) => normalizar(p.descricao).includes(termo))
      .forEach((p) => {
        const quantidade = Number(p.quantidade);
        const dia = Number(p.diaSemana) - 1;
        const hora = Number(p.hora);

        if (dia >= 0 && dia < 7) dias[dia] += quantidade;
        if (hora >= 0 && hora < 24) horas[hora] += quantidade;
      });

    const total = horas.reduce((soma, quantidade) => soma + quantidade, 0);

    return {
      total,
      picoDia: total > 0 ? indiceDoMaior(dias) : null,
      picoHora: total > 0 ? indiceDoMaior(horas) : null,
      dadosDias: [
        ['Dia', 'Unidades'],
        ...dias.map((quantidade, i) => [DIAS_SEMANA[i], quantidade]),
      ],
      dadosHoras: [
        ['Hora', 'Unidades'],
        ...horas.map((quantidade, i) => [formatarHora(i), quantidade]),
      ],
    };
  }, [produtosHorario, busca]);

  const topProdutosHora = useMemo(() => {
    if (horaSelecionada === null) return [];

    const somaPorProduto = new Map();
    produtosHorario
      .filter((p) => Number(p.hora) === horaSelecionada)
      .forEach((p) => {
        const atual = somaPorProduto.get(p.idProduto) || {
          descricao: p.descricao,
          quantidade: 0,
        };
        atual.quantidade += Number(p.quantidade);
        somaPorProduto.set(p.idProduto, atual);
      });

    return [...somaPorProduto.values()]
      .sort((a, b) => b.quantidade - a.quantidade)
      .slice(0, 5);
  }, [produtosHorario, horaSelecionada]);

  const colunasPodio = useMemo(
    () =>
      [1, 0, 2]
        .filter((posicao) => topProdutosHora[posicao])
        .map((posicao) => ({
          posicao,
          produto: topProdutosHora[posicao],
          ...ESTILO_PODIO[posicao],
        })),
    [topProdutosHora],
  );

  const restantePodio = topProdutosHora.slice(3);

  const filtrar = useCallback(async () => {
    setCarregando(true);

    const corpo = {
      IdEmpresa: id_empresa,
      DataInicial: periodo.inicio,
      DataFinal: periodo.fim,
    };

    await Promise.all([
      api
        .post('pedido/relatoriosGeral/produtosPorHorario', corpo)
        .then((res) => setProdutosHorario(res.data)),
      api
        .post('pedido/relatoriosGeral/vendasPorPeriodoHorario', corpo)
        .then((res) => setLinhas(res.data)),
    ])
      .catch((erro) => {
        const mensagem = erro.response?.data?.message;

        setLinhas([]);
        setProdutosHorario([]);
        NotificationManager.error(
          (Array.isArray(mensagem) ? mensagem.join(', ') : mensagem) ||
            'Não foi possível carregar o relatório. Tente novamente.',
          'Erro',
        );
      })
      .finally(() => setCarregando(false));
  }, [periodo, id_empresa]);

  useEffect(() => {
    filtrar();
  }, [filtrar]);

  const buscar = () => {
    if (!dataInicio || !dataFim) {
      const mes = periodoDoMes();
      setDataInicio(mes.inicio);
      setDataFim(mes.fim);
      setPeriodo(mes);
      return;
    }

    if (dataInicio > dataFim) {
      NotificationManager.warning(
        'A data inicial não pode ser maior que a data final.',
        'Aviso',
      );
      return;
    }

    setPeriodo({ inicio: dataInicio, fim: dataFim });
  };

  const opcoesBase = {
    legend: 'none',
    backgroundColor: 'transparent',
    colors: ['#2a78d6'],
    chartArea: { left: 48, right: 16, top: 16, bottom: 48 },
    hAxis: {
      textStyle: { color: theme.colors.textSecondary, fontSize: 11 },
    },
    vAxis: {
      minValue: 0,
      viewWindow: { min: 0 },
      format: '0',
      textStyle: { color: theme.colors.textSecondary, fontSize: 11 },
      gridlines: { color: theme.colors.lightGrayOpacity },
    },
  };

  const opcoesCurva = {
    ...opcoesBase,
    curveType: 'function',
    lineWidth: 3,
    areaOpacity: 0.2,
    pointSize: 5,
  };

  const mostrarGeral = vendasProduto === null && horaSelecionada === null;
  const mostrarProduto = vendasProduto !== null && vendasProduto.total > 0;
  const mostrarProdutoSemVendas =
    vendasProduto !== null && vendasProduto.total === 0;
  const mostrarTopHora = horaSelecionada !== null;

  return (
    <PageWrapper>
      <PageHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PageTitle>Vendas por Período e Horário</PageTitle>
          <Ajuda texto="Mostra quando os clientes mais compram, por dia da semana e por horário, considerando apenas pedidos concluídos no período. Use a busca para ver quando um produto vende mais, ou escolha um horário para ver os produtos mais vendidos nele." />
        </div>
        <FiltrosExplorar>
          <AutoComplete
            data={sugestoesProdutos}
            value={busca}
            onChange={aoBuscarProduto}
            filterBy={(valor, item) =>
              normalizar(item.label).includes(normalizar(valor))
            }
            placeholder="Buscar produto..."
            style={{ width: 260 }}
          />
          <CustomSelectPicker
            data={OPCOES_HORA}
            value={horaSelecionada}
            onChange={aoEscolherHora}
            placeholder="Produtos por horário"
            style={{ width: 200 }}
          />
        </FiltrosExplorar>
        <RelatorioPeriodoVendas style={{ alignItems: 'flex-end', gap: 8 }}>
          <RelatorioFiltroPeriodo
            dataInicio={dataInicio}
            dataFim={dataFim}
            onChangeDataInicio={setDataInicio}
            onChangeDataFim={setDataFim}
          />
          <CustomButton
            appearance="primary"
            loading={carregando}
            onClick={buscar}
          >
            Buscar
          </CustomButton>
        </RelatorioPeriodoVendas>
      </PageHeader>

      <DashboardGrid>
        {mostrarGeral && (
          <>
            <ChartCard>
              <ChartTitleRow>
                <KpiLabel>Vendas por dia da semana</KpiLabel>
                <Ajuda texto="Quantidade de pedidos concluídos em cada dia da semana, somando todo o período selecionado. Ajuda a decidir em quais dias reforçar a equipe." />
              </ChartTitleRow>
              <Chart
                chartLanguage="pt"
                chartType="ColumnChart"
                width="100%"
                height="320px"
                loader={<div>Carregando gráfico...</div>}
                data={dadosDias}
                options={opcoesBase}
              />
            </ChartCard>

            <ChartCard>
              <ChartTitleRow>
                <KpiLabel>Vendas por hora do dia</KpiLabel>
                <Ajuda texto="Quantidade de pedidos concluídos em cada horário, somando todos os dias do período. O ponto mais alto da curva é o horário de pico. Use para montar a escala e planejar promoções." />
              </ChartTitleRow>
              <Chart
                chartLanguage="pt"
                chartType="AreaChart"
                width="100%"
                height="320px"
                loader={<div>Carregando gráfico...</div>}
                data={dadosHoras}
                options={opcoesCurva}
              />
            </ChartCard>
          </>
        )}

        {mostrarProduto && (
          <>
            <ChartCard>
              <ChartTitleRow>
                <KpiLabel>
                  {`"${busca}" por dia da semana. Pico: ${DIAS_SEMANA[vendasProduto.picoDia]}`}
                </KpiLabel>
                <Ajuda texto="Unidades vendidas do produto buscado em cada dia da semana, somando o período. Se a busca combinar com mais de um produto, as unidades são somadas." />
              </ChartTitleRow>
              <Chart
                chartLanguage="pt"
                chartType="ColumnChart"
                width="100%"
                height="320px"
                loader={<div>Carregando gráfico...</div>}
                data={vendasProduto.dadosDias}
                options={opcoesBase}
              />
            </ChartCard>

            <ChartCard>
              <ChartTitleRow>
                <KpiLabel>
                  {`"${busca}" por hora do dia. Pico: ${formatarHora(vendasProduto.picoHora)}`}
                </KpiLabel>
                <Ajuda texto="Unidades vendidas do produto buscado em cada horário, somando todos os dias do período. O pico indica o melhor horário para promovê-lo." />
              </ChartTitleRow>
              <Chart
                chartLanguage="pt"
                chartType="AreaChart"
                width="100%"
                height="320px"
                loader={<div>Carregando gráfico...</div>}
                data={vendasProduto.dadosHoras}
                options={opcoesCurva}
              />
            </ChartCard>
          </>
        )}

        {mostrarProdutoSemVendas && (
          <ExplorarCard>
            <KpiLabel>
              Nenhuma venda encontrada para esse produto no período.
            </KpiLabel>
          </ExplorarCard>
        )}

        {mostrarTopHora && (
          <ExplorarCard>
            <ChartTitleRow>
              <KpiLabel>
                {`Produtos mais vendidos às ${formatarHora(horaSelecionada)}`}
              </KpiLabel>
              <Ajuda texto="Os 5 produtos com mais unidades vendidas na faixa de uma hora escolhida, somando todos os dias do período. Os 3 primeiros aparecem no pódio." />
            </ChartTitleRow>
            {topProdutosHora.length === 0 && (
              <KpiLabel>Nenhuma venda nesse horário no período.</KpiLabel>
            )}
            {topProdutosHora.length > 0 && (
              <>
                <Podio>
                  {colunasPodio.map((coluna) => (
                    <PodioColuna key={coluna.produto.descricao}>
                      <PodioNome title={coluna.produto.descricao}>
                        {coluna.produto.descricao}
                      </PodioNome>
                      <PodioQuantidade>
                        {`${coluna.produto.quantidade} un.`}
                      </PodioQuantidade>
                      <PodioBloco $altura={coluna.altura} $color={coluna.cor}>
                        {`${coluna.posicao + 1}º`}
                      </PodioBloco>
                    </PodioColuna>
                  ))}
                </Podio>
                {restantePodio.length > 0 && (
                  <PodioRestante>
                    {restantePodio.map((produto, indice) => (
                      <PodioRestanteLinha key={produto.descricao}>
                        <span>{`${indice + 4}º ${produto.descricao}`}</span>
                        <span>{`${produto.quantidade} un.`}</span>
                      </PodioRestanteLinha>
                    ))}
                  </PodioRestante>
                )}
              </>
            )}
          </ExplorarCard>
        )}
      </DashboardGrid>
    </PageWrapper>
  );
};

export default VendasPorPeriodoHorario;
